import { create } from 'zustand';
import { api } from '../services/api';
import { CONSENT_VERSION } from '../consent';
import type { SignatureChoice, SignatureStyle } from '../components/SignaturePad';

interface Document {
  id: string;
  title: string;
  status: string;
  created_at: string;
  return_url_success?: string;
  return_url_error?: string;
  return_url_cancel?: string;
  signature_style?: SignatureStyle;
}

interface Signer {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  role?: string;
  status: string;
  otp_channel: 'email' | 'sms' | 'email_sms';
}

/** Où en est l'authentification : le lien reçu prouve déjà son canal, un code prouve le reste */
interface AuthProgress {
  authenticated: boolean;
  required: Array<'email' | 'sms'>;
  pending: Array<'email' | 'sms'>;
}

interface Branding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  company_name?: string;
}

interface SignatureField {
  page: number;
  source: 'coordinates' | 'marker' | 'signature_page';
}

interface SignatureState {
  document: Document | null;
  signatureField: SignatureField | null;
  signer: Signer | null;
  branding: Branding | null;
  sessionToken: string | null;

  loadSession: (token: string) => Promise<void>;
  markViewed: () => Promise<AuthProgress>;
  verifyOTP: (code: string) => Promise<void>;
  /** `allSigned` : ce signataire est le dernier, le document signé sera téléchargeable */
  signDocument: (signature?: SignatureChoice | null) => Promise<{ allSigned: boolean }>;
  declineDocument: (reason: string) => Promise<void>;
}

export const useSignatureStore = create<SignatureState>((set, get) => ({
  document: null,
  signatureField: null,
  signer: null,
  branding: null,
  sessionToken: null,

  loadSession: async (token: string) => {
    try {
      const response = await api.post('/session/info', { token });
      const { document, signer, branding, signature_field } = response.data;

      set({
        document,
        signatureField: signature_field ?? null,
        signer,
        branding,
        sessionToken: token,
      });
    } catch (error) {
      throw new Error('Session invalide ou expirée');
    }
  },

  markViewed: async () => {
    const { sessionToken } = get();
    if (!sessionToken) throw new Error('No active session');

    try {
      const response = await api.post('/session/viewed', { token: sessionToken });
      const progress: AuthProgress = {
        authenticated: Boolean(response.data.authenticated),
        required: response.data.required_factors ?? [],
        pending: response.data.pending_factors ?? [],
      };

      // Update signer status locally
      set((state) => ({
        signer: state.signer ? { ...state.signer, status: progress.authenticated ? 'authenticated' : 'viewed' } : null,
      }));
      return progress;
    } catch (error) {
      throw new Error('Erreur lors de la confirmation de lecture');
    }
  },

  verifyOTP: async (code: string) => {
    const { sessionToken, signer } = get();
    if (!sessionToken || !signer) throw new Error('No active session');

    try {
      await api.post(`/signers/${signer.id}/otp/verify`, { token: sessionToken, code });

      set((state) => ({
        signer: state.signer ? { ...state.signer, status: 'authenticated' } : null,
      }));
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Code invalide');
    }
  },

  signDocument: async (signature) => {
    const { sessionToken } = get();
    if (!sessionToken) throw new Error('No active session');

    try {
      // La version du texte de consentement affiche est enregistree avec la signature,
      // ainsi que l'apparence choisie (tracé ou nom saisi)
      const response = await api.post('/session/sign', {
        token: sessionToken,
        consent_version: CONSENT_VERSION,
        ...(signature ? { signature } : {}),
      });

      set((state) => ({
        signer: state.signer ? { ...state.signer, status: 'signed' } : null,
        document: state.document ? { ...state.document, status: 'completed' } : null,
      }));
      return { allSigned: Boolean(response.data?.all_signed) };
    } catch (error: any) {
      throw new Error(error?.response?.data?.error?.message || 'Erreur lors de la signature du document');
    }
  },

  declineDocument: async (reason: string) => {
    const { sessionToken } = get();
    if (!sessionToken) throw new Error('No active session');

    try {
      await api.post('/session/decline', { token: sessionToken, reason });

      set((state) => ({
        signer: state.signer ? { ...state.signer, status: 'declined' } : null,
      }));
    } catch (error) {
      throw new Error('Erreur lors du refus');
    }
  },
}));
